import React from "react";
import { Check, Zap } from "lucide-react";

const TIERS = [
  {
    name: "Economy",
    turnaround: "45 business days",
    maxValue: "$500",
    price: "$15",
    popular: false,
    perks: ["AI grade w/ 4 subgrades", "Blue slab & label", "Public cert ledger", "Standard shipping"],
  },
  {
    name: "Standard",
    turnaround: "20 business days",
    maxValue: "$2,500",
    price: "$35",
    popular: true,
    perks: ["Everything in Economy", "Priority AI queue", "High-res digital scans", "Insured return shipping"],
  },
  {
    name: "Express",
    turnaround: "7 business days",
    maxValue: "$10,000",
    price: "$95",
    popular: false,
    perks: ["Everything in Standard", "White-glove intake", "Sub-grade report PDF", "Express return shipping"],
  },
  {
    name: "Premium",
    turnaround: "48 hours",
    maxValue: "Unlimited",
    price: "$295",
    popular: false,
    perks: ["Everything in Express", "Dedicated concierge", "Custom slab engraving", "Hand-delivered option"],
  },
];

export const Tiers = () => {
  return (
    <section id="tiers" className="relative py-24 md:py-32" data-testid="tiers-section">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/4 w-[500px] h-[500px] rounded-full bg-brand/10 blur-[140px]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <div className="chip mb-6">Grading Tiers</div>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]">
            Built for every collector.
          </h2>
          <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed max-w-2xl">
            Choose a service level based on your card's value and how fast you need it in a slab. Every tier gets
            the same AI grade — only speed and handling differ.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {TIERS.map((t) => (
            <div
              key={t.name}
              data-testid={`tier-card-${t.name.toLowerCase()}`}
              className={`relative glass rounded-2xl p-7 transition-all duration-500 hover:-translate-y-2 ${
                t.popular ? "glow-border" : "hover:border-brand/30"
              }`}
            >
              {t.popular && (
                <div className="absolute -top-3 left-7 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.25em] font-bold px-3 py-1 rounded-full bg-brand text-ink-900">
                  <Zap size={10} /> Most Popular
                </div>
              )}
              <h3 className="font-display text-2xl font-bold tracking-tight">{t.name}</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-4xl font-black text-white">{t.price}</span>
                <span className="text-white/40 text-sm">/ card</span>
              </div>
              <div className="mt-5 space-y-1">
                <div className="text-xs text-white/40 uppercase tracking-[0.2em]">Turnaround</div>
                <div className="text-brand font-semibold">{t.turnaround}</div>
              </div>
              <div className="mt-4 space-y-1">
                <div className="text-xs text-white/40 uppercase tracking-[0.2em]">Max Declared Value</div>
                <div className="text-white/90 font-semibold">{t.maxValue}</div>
              </div>
              <div className="my-6 h-px bg-white/10" />
              <ul className="space-y-3">
                {t.perks.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm text-white/70">
                    <Check size={16} className="text-brand shrink-0 mt-0.5" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Tiers;
